import { ref, computed, inject, watch } from 'vue'
import { defineStore } from 'pinia'
import type {
  Contract,
  ContractDetail,
  GetContractsResponse,
  GetContractDetailResponse,
  AccountManager,
  GetDashboardInfoResponse,
  ContractOverview,
  InsuranceCasesOverview,
  MessagesOverview,
  Notice,
  Client,
  ClientInformation,
  GetClientsForCustomerResponse,
  GetClientInformationResponse,
  GetDocumentsResponse,
  Document,
  GetSubcontractsResponse,
  InsuranceCase,
  GetInsuranceCasesResponse,
  Payment,
  GetPaymentsResponse,
  GetMessagesResponse,
  Message,
  GetInsuranceCaseDetailResponse,
  InsuranceCaseDetail,
} from '@/types/customer'
import { ContractType, MessageStatus } from '@/types/customer'
import type { MessageState } from '@/types/message'
import type { SetMessageStatusResponse } from '@/types/message'
import type { CustomerDataService } from '@/types/customer-data-service'
import { SearchTarget } from '@/types/search'
import { useAuth } from '@/composables/useAuth'
import { TYPES } from '@/container/types'
import { handleStoreError } from '@/utils/errorHandler'
import router from '@/router'
import {
  advanceViewRequests,
  currentViewEpoch,
  isRequestCanceled,
  isStaleView,
} from '@/utils/viewRequests'

interface FilterState {
  active: boolean
  expired: boolean
  canceled: boolean
}

interface CaseFilterState {
  open: boolean
  closed: boolean
  postponed: boolean
}

interface MessageStatusFilterState {
  created: boolean
  read: boolean
  archived: boolean
}

interface RouteContext {
  clientId: number | null
  contractId: number | null
  contractType: ContractType | null
  subcontractId: number | null
  caseId: number | null
}

// Private factory function - not exported
const createCustomerStore = (customerDataService: CustomerDataService) =>
  defineStore('customer', () => {
    const { isAuthenticated, handleAuthFailure } = useAuth()

    const loading = {
      contracts: ref(false),
      contractDetail: ref(false),
      subContractDetail: ref(false),
      contractDocuments: ref(false),
      messageAttachments: ref(false),
      generalAttachments: ref(false),
      clientAttachments: ref(false),
      meeting: ref(false),
      dashboard: ref(false),
      clientInformation: ref(false),
      subcontracts: ref(false),
      insuranceCases: ref(false),
      insuranceCaseDetail: ref(false),
      payments: ref(false),
      search: ref(false),
      message: ref(false),
      messages: ref(false),
      changePassword: ref(false),
    }

    // Flag to prevent route updates during hierarchy loading
    const isLoadingHierarchy = ref(false)

    // State
    const contracts = ref<Contract[] | null>(null)
    const contractDetail = ref<ContractDetail | null>(null)
    const subContractDetail = ref<ContractDetail | null>(null)
    const contractDocuments = ref<Document[] | null>(null)
    const messageAttachments = ref<Document[] | null>(null)
    const generalAttachments = ref<Document[] | null>(null)
    const clientAttachments = ref<Document[] | null>(null)
    const dashboardInfo = ref<GetDashboardInfoResponse | null>(null)
    const clients = ref<Client[] | null>(null)
    let clientsLoadInFlight: Promise<void> | null = null
    const clientInformation = ref<ClientInformation | null>(null)
    const subcontracts = ref<Contract[] | null>(null)
    const insuranceCases = ref<InsuranceCase[] | null>(null)
    const insuranceCaseDetail = ref<InsuranceCaseDetail | null>(null)
    const payments = ref<Payment[] | null>(null)
    const messages = ref<Message[] | null>(null)
    const searchResults = ref<{ contracts: Contract[]; insuranceCases: InsuranceCase[] } | null>(
      null,
    )

    // Message state
    const message = ref<MessageState>({
      title: '',
      text: '',
      attachments: [],
    })

    // Reactive trigger for client changes - components can watch this
    const clientChangeTrigger = ref(0)

    // Route context - maintains all route IDs for single source of truth
    const routeContext = ref<RouteContext>({
      clientId: null,
      contractId: null,
      contractType: null,
      subcontractId: null,
      caseId: null,
    })

    // Open record only. Filling in a parent contract id must not count as leaving the view.
    function openRecordKey(ctx: RouteContext): string {
      const clientId = ctx.clientId ?? ''
      if (ctx.caseId != null) return `${clientId}:case:${ctx.caseId}`
      if (ctx.subcontractId != null) return `${clientId}:sub:${ctx.subcontractId}`
      if (ctx.contractId != null) return `${clientId}:contract:${ctx.contractId}`
      return `${clientId}`
    }

    watch(
      () => openRecordKey(routeContext.value),
      () => {
        advanceViewRequests()
      },
      { flush: 'sync' },
    )

    function beginViewLoad() {
      const epoch = currentViewEpoch()
      return {
        current: () => !isStaleView(epoch),
      }
    }

    function droppedViewLoad(load: { current: () => boolean }, error?: unknown): boolean {
      return !load.current() || (error !== undefined && isRequestCanceled(error))
    }

    // Full Client row for UI (name, etc.); needs `clients` loaded — use UserInfo/App ensureClientsLoaded or getClients().
    // For API calls that only need an id, use routeContext.clientId instead.
    const selectedClient = computed<Client | null>({
      get: () => {
        const clientId = routeContext.value.clientId
        if (clientId && clients.value) {
          return clients.value.find((c) => c.id === clientId) || clients.value[0] || null
        }
        return clients.value?.[0] || null
      },
      set: (value: Client | null) => {
        if (value) {
          routeContext.value.clientId = value.id
        } else {
          routeContext.value.clientId = null
        }
      },
    })

    // Computed - getters
    const isLoadingContracts = computed(() => loading.contracts.value)
    const isLoadingContractDetail = computed(() => loading.contractDetail.value)
    const isLoadingSubContractDetail = computed(() => loading.subContractDetail.value)
    const isLoadingContractDocuments = computed(() => loading.contractDocuments.value)
    const isLoadingMessageAttachments = computed(() => loading.messageAttachments.value)
    const isLoadingGeneralAttachments = computed(() => loading.generalAttachments.value)
    const isLoadingClientAttachments = computed(() => loading.clientAttachments.value)
    const isLoadingMeeting = computed(() => loading.meeting.value)
    const isLoadingDashboard = computed(() => loading.dashboard.value)
    const isLoadingClientInformation = computed(() => loading.clientInformation.value)
    const isLoadingSubcontracts = computed(() => loading.subcontracts.value)
    const isLoadingInsuranceCases = computed(() => loading.insuranceCases.value)
    const isLoadingInsuranceCaseDetail = computed(() => loading.insuranceCaseDetail.value)
    const isLoadingPayments = computed(() => loading.payments.value)
    const isLoadingSearch = computed(() => loading.search.value)
    const isLoadingMessage = computed(() => loading.message.value)
    const isLoadingMessages = computed(() => loading.messages.value)
    const isLoadingChangePassword = computed(() => loading.changePassword.value)

    const accountManager = computed<AccountManager[]>(
      () => dashboardInfo.value?.dashboard?.accountManager || [],
    )
    const contractsOverview = computed<ContractOverview[] | null>(
      () => dashboardInfo.value?.dashboard?.contractsOverview || null,
    )
    const insuranceOverview = computed<InsuranceCasesOverview[] | null>(
      () => dashboardInfo.value?.dashboard?.insurancesOverview || null,
    )
    const messagesOverview = computed<MessagesOverview[] | null>(
      () => dashboardInfo.value?.dashboard?.messagesOverview || null,
    )
    const noticeBoard = computed<Notice[] | null>(
      () => dashboardInfo.value?.dashboard?.noticeBoard || null,
    )

    /**
     * Client-scoped loads: missing client id → replace route with `/` (root redirect handles selection).
     * @returns Client id, or `null` after scheduling navigation — caller must clear its slice and return.
     */
    function requireClientIdOrRedirectToRoot(): number | null {
      const id = routeContext.value.clientId
      if (!id) {
        void router.replace('/')
        return null
      }
      return id
    }

    // Actions
    async function loadContracts(filters: FilterState): Promise<void> {
      if (!isAuthenticated.value) {
        await handleAuthFailure()
        contracts.value = null
        return
      }

      const clientId = requireClientIdOrRedirectToRoot()
      if (clientId === null) {
        contracts.value = null
        return
      }

      loading.contracts.value = true
      const viewLoad = beginViewLoad()

      try {
        const response: GetContractsResponse = await customerDataService.getContracts(
          clientId,
          filters.active,
          filters.expired,
          filters.canceled,
        )
        if (droppedViewLoad(viewLoad)) return

        if (response.success) {
          contracts.value = response.contracts || []
        } else {
          // Legacy response format
          throw Error(response.message || 'Failed to fetch contracts')
        }
      } catch (err) {
        if (droppedViewLoad(viewLoad, err)) return
        handleStoreError(err as Error, {
          errorType: 'loadContracts',
          redirectTo: '/',
        })
        contracts.value = null
      } finally {
        if (viewLoad.current()) loading.contracts.value = false
      }
    }

    async function loadSubcontracts(filters: FilterState): Promise<void> {
      if (!isAuthenticated.value) {
        await handleAuthFailure()
        subcontracts.value = null
        return
      }

      const clientId = requireClientIdOrRedirectToRoot()
      if (clientId === null) {
        subcontracts.value = null
        return
      }

      const contractId = routeContext.value.contractId ?? null
      const contractType: ContractType | null = routeContext.value.contractType ?? null

      if (contractId === null || contractType === null) {
        subcontracts.value = null
        return
      }

      if (contractType !== ContractType.multi && contractType !== ContractType.kasko) {
        subcontracts.value = null
        return
      }

      loading.subcontracts.value = true
      const viewLoad = beginViewLoad()

      try {
        const response: GetSubcontractsResponse = await customerDataService.getSubcontracts(
          clientId,
          contractId,
          filters.active,
          filters.expired,
          filters.canceled,
        )
        if (droppedViewLoad(viewLoad)) return
        if (response.success) {
          subcontracts.value = response.subcontracts || []
        } else {
          // Legacy response format
          throw Error(response.message || 'Failed to fetch subcontracts')
        }
      } catch (err) {
        if (droppedViewLoad(viewLoad, err)) return
        handleStoreError(err as Error, {
          errorType: 'loadSubcontracts',
          redirectTo: '/',
        })
        subcontracts.value = null
      } finally {
        if (viewLoad.current()) loading.subcontracts.value = false
      }
    }

    async function loadSubContractDetail(): Promise<void> {
      if (!isAuthenticated.value) {
        await handleAuthFailure()
        subContractDetail.value = null
        return
      }

      const clientId = requireClientIdOrRedirectToRoot()
      if (clientId === null) {
        subContractDetail.value = null
        return
      }

      // Only require subcontractId (don't require contractId initially)
      const subcontractId = routeContext.value.subcontractId ?? null

      if (!subcontractId) {
        subContractDetail.value = null
        return
      }

      // Load subcontract detail first (don't require parent context)
      isLoadingHierarchy.value = true
      loading.subContractDetail.value = true
      subContractDetail.value = null
      const viewLoad = beginViewLoad()

      try {
        let loadedSubcontract: ContractDetail | null = null

        const response: GetContractDetailResponse = await customerDataService.getSubcontractDetail(
          clientId,
          subcontractId,
        )
        if (droppedViewLoad(viewLoad)) return

        if (response.success) {
          loadedSubcontract = response.contract || null
        } else {
          // Legacy response format
          throw Error(response.message || 'Failed to fetch subcontract detail')
        }

        // Extract parent hierarchy data from loaded subcontract and update routeContext
        if (loadedSubcontract) {
          subContractDetail.value = loadedSubcontract
          updateRouteContext(
            loadedSubcontract.parentContractId ?? null,
            loadedSubcontract.parentContractType ?? null,
            subcontractId,
          )
        }

        // Parent contract is context for breadcrumbs, not part of showing this subcontract.
        if (viewLoad.current()) loading.subContractDetail.value = false
        await ensureParentContext('subcontract')
      } catch (err) {
        if (droppedViewLoad(viewLoad, err)) return
        handleStoreError(err as Error, {
          errorType: 'loadSubContractDetail',
        })
        subContractDetail.value = null
      } finally {
        if (viewLoad.current()) {
          isLoadingHierarchy.value = false
          loading.subContractDetail.value = false
        }
      }
    }

    async function loadContractDetail(): Promise<void> {
      if (!isAuthenticated.value) {
        await handleAuthFailure()
        contractDetail.value = null
        return
      }

      const clientId = requireClientIdOrRedirectToRoot()
      if (clientId === null) {
        contractDetail.value = null
        return
      }

      const contractId = routeContext.value.contractId ?? null

      if (contractId === null) {
        contractDetail.value = null
        return
      }

      const viewLoad = beginViewLoad()

      // If contractType is not set, try to find it from contracts list
      let contractType = routeContext.value.contractType
      if (contractType === null) {
        if (!contracts.value) {
          await loadContracts({ active: true, expired: true, canceled: true })
        }
        if (droppedViewLoad(viewLoad)) return
        const contract = contracts.value?.find((c) => c.id === contractId)
        if (contract) {
          contractType = contract.type
          // Update routeContext with the found contractType
          routeContext.value = {
            ...routeContext.value,
            contractType: contractType,
          }
        }
      }

      // If still no contractType, we need it to call the API
      if (contractType === null) {
        contractDetail.value = null
        return
      }

      loading.contractDetail.value = true
      contractDetail.value = null

      try {
        const response: GetContractDetailResponse = await customerDataService.getContractDetail(
          clientId,
          contractId,
          contractType,
        )
        if (droppedViewLoad(viewLoad)) return

        if (response.success) {
          contractDetail.value = response.contract || null
          // Ensure contractType is set in routeContext if not already set
          if (response.contract && routeContext.value.contractType === null) {
            routeContext.value = {
              ...routeContext.value,
              contractType: response.contract.type,
            }
          }
        } else {
          // Legacy response format
          throw Error(response.message || 'Failed to fetch contract detail')
        }
      } catch (err) {
        if (droppedViewLoad(viewLoad, err)) return
        handleStoreError(err as Error, {
          errorType: 'loadContractDetail',
        })
        contractDetail.value = null
      } finally {
        if (viewLoad.current()) loading.contractDetail.value = false
      }
    }

    async function loadDocuments(): Promise<void> {
      if (!isAuthenticated.value) {
        await handleAuthFailure()
        contractDocuments.value = null
        return
      }

      const clientId = requireClientIdOrRedirectToRoot()
      if (clientId === null) {
        contractDocuments.value = null
        return
      }

      if (
        !routeContext.value.caseId &&
        !routeContext.value.subcontractId &&
        !routeContext.value.contractId
      ) {
        contractDocuments.value = null
        return
      }

      loading.contractDocuments.value = true
      const viewLoad = beginViewLoad()

      try {
        let response: GetDocumentsResponse
        if (routeContext.value.caseId) {
          response = await customerDataService.getInsuranceCaseDocuments(
            clientId,
            routeContext.value.caseId,
          )
        } else if (routeContext.value.subcontractId) {
          response = await customerDataService.getSubcontractDocuments(
            clientId,
            routeContext.value.subcontractId,
          )
        } else {
          response = await customerDataService.getContractDocuments(
            clientId,
            routeContext.value.contractId!,
          )
        }

        if (droppedViewLoad(viewLoad)) return
        if (response.success) {
          contractDocuments.value = response.documents || []
        } else {
          // Legacy response format
          throw Error(response.message || 'Failed to fetch documents')
        }
      } catch (err) {
        if (droppedViewLoad(viewLoad, err)) return
        handleStoreError(err as Error, {
          errorType: 'loadDocuments',
        })
        contractDocuments.value = null
      } finally {
        if (viewLoad.current()) loading.contractDocuments.value = false
      }
    }

    async function loadMessageAttachments(messageId: number): Promise<void> {
      if (!isAuthenticated.value) {
        await handleAuthFailure()
        messageAttachments.value = null
        return
      }

      const clientId = requireClientIdOrRedirectToRoot()
      if (clientId === null) {
        messageAttachments.value = null
        return
      }

      if (!messageId) {
        messageAttachments.value = null
        return
      }

      loading.messageAttachments.value = true
      const viewLoad = beginViewLoad()

      try {
        const response: GetDocumentsResponse = await customerDataService.getMessageAttachments(
          clientId,
          messageId,
        )
        if (droppedViewLoad(viewLoad)) return

        if (response.success) {
          messageAttachments.value = response.documents || []
        } else {
          // Legacy response format
          throw Error(response.message || 'Failed to fetch message attachments')
        }
      } catch (err) {
        if (droppedViewLoad(viewLoad, err)) return
        handleStoreError(err as Error, {
          errorType: 'loadMessageAttachments',
        })
        messageAttachments.value = null
      } finally {
        if (viewLoad.current()) loading.messageAttachments.value = false
      }
    }

    async function loadGeneralAttachments(): Promise<void> {
      if (!isAuthenticated.value) {
        await handleAuthFailure()
        generalAttachments.value = null
        return
      }

      loading.generalAttachments.value = true
      const viewLoad = beginViewLoad()

      try {
        const response: GetDocumentsResponse = await customerDataService.getGeneralAttachments()
        if (droppedViewLoad(viewLoad)) return

        if (response.success) {
          generalAttachments.value = response.documents || []
        } else {
          throw Error(response.message || 'Failed to fetch general attachments')
        }
      } catch (err) {
        if (droppedViewLoad(viewLoad, err)) return
        handleStoreError(err as Error, {
          errorType: 'loadGeneralAttachments',
        })
        generalAttachments.value = null
      } finally {
        if (viewLoad.current()) loading.generalAttachments.value = false
      }
    }

    async function loadClientAttachments(): Promise<void> {
      if (!isAuthenticated.value) {
        await handleAuthFailure()
        clientAttachments.value = null
        return
      }

      const clientId = requireClientIdOrRedirectToRoot()
      if (clientId === null) {
        clientAttachments.value = null
        return
      }

      loading.clientAttachments.value = true
      const viewLoad = beginViewLoad()

      try {
        const response: GetDocumentsResponse =
          await customerDataService.getClientAttachments(clientId)
        if (droppedViewLoad(viewLoad)) return

        if (response.success) {
          clientAttachments.value = response.documents || []
        } else {
          throw Error(response.message || 'Failed to fetch client attachments')
        }
      } catch (err) {
        if (droppedViewLoad(viewLoad, err)) return
        handleStoreError(err as Error, {
          errorType: 'loadClientAttachments',
        })
        clientAttachments.value = null
      } finally {
        if (viewLoad.current()) loading.clientAttachments.value = false
      }
    }

    async function loadInsuranceCases(filters?: CaseFilterState): Promise<void> {
      if (!isAuthenticated.value) {
        await handleAuthFailure()
        return
      }

      const clientId = requireClientIdOrRedirectToRoot()
      if (clientId === null) {
        insuranceCases.value = null
        return
      }

      const contractId = routeContext.value.contractId ?? null
      const subcontractId = routeContext.value.subcontractId ?? null
      const type = routeContext.value.contractType ?? null

      loading.insuranceCases.value = true
      const viewLoad = beginViewLoad()

      try {
        let response: GetInsuranceCasesResponse
        if (subcontractId) {
          response = await customerDataService.getInsuranceCases(
            clientId,
            subcontractId,
            ContractType.subcontract,
            filters,
          )
        } else if (!contractId) {
          response = await customerDataService.getInsuranceCases(
            clientId,
            undefined,
            undefined,
            filters,
          )
        } else if (contractId && type !== null) {
          response = await customerDataService.getInsuranceCases(
            clientId,
            contractId,
            type,
            filters,
          )
        } else {
          insuranceCases.value = null
          return
        }

        if (droppedViewLoad(viewLoad)) return
        if (response.success) {
          insuranceCases.value = response.insuranceCases || []
        } else {
          // Legacy response format
          throw Error(response.message || 'Failed to fetch insurance cases')
        }
      } catch (err) {
        if (droppedViewLoad(viewLoad, err)) return
        handleStoreError(err as Error, {
          errorType: 'loadInsuranceCases',
        })
        insuranceCases.value = null
      } finally {
        if (viewLoad.current()) loading.insuranceCases.value = false
      }
    }

    // Helper function to update routeContext from insurance case hierarchy data
    function updateRouteContext(
      contractId: number | null,
      contractType: ContractType | null,
      subcontractId: number | null,
    ): void {
      // Only update if values actually changed to avoid unnecessary route updates
      const contractIdChanged = contractId !== null && contractId !== routeContext.value.contractId
      const contractTypeChanged =
        contractType !== null && contractType !== routeContext.value.contractType
      const subcontractIdChanged =
        subcontractId !== null && subcontractId !== routeContext.value.subcontractId

      if (contractIdChanged || contractTypeChanged || subcontractIdChanged) {
        routeContext.value = {
          ...routeContext.value,
          contractId: contractId ?? routeContext.value.contractId,
          contractType: contractType ?? routeContext.value.contractType,
          subcontractId: subcontractId ?? routeContext.value.subcontractId,
        }
      }
    }

    // Helper function to ensure parent context is loaded based on hierarchy
    async function ensureParentContext(
      objectType: 'subcontract' | 'insuranceCase',
    ): Promise<boolean> {
      const ctx = routeContext.value

      // If we have a caseId, ensure parent Contract (and Subcontract if applicable)
      if (objectType === 'insuranceCase' && ctx.caseId) {
        if (ctx.subcontractId) {
          await loadSubContractDetail()
        } else if (ctx.contractId) {
          await loadContractDetail()
        }
        return true
      }
      // If we have a subcontractId, ensure parent Contract
      if (objectType === 'subcontract' && ctx.subcontractId) {
        if (ctx.contractId != null && contractDetail.value?.id === ctx.contractId) {
          return true
        }
        await loadContractDetail()
        return true
      }
      return false
    }

    async function loadInsuranceCaseDetail(): Promise<void> {
      if (!isAuthenticated.value) {
        await handleAuthFailure()
        insuranceCaseDetail.value = null
        return
      }

      const clientId = requireClientIdOrRedirectToRoot()
      if (clientId === null) {
        insuranceCaseDetail.value = null
        return
      }

      // Always use caseId from route context
      const caseId = routeContext.value.caseId
      if (!caseId) {
        insuranceCaseDetail.value = null
        return
      }

      // Load insurance case detail first (don't require parent context)
      isLoadingHierarchy.value = true
      loading.insuranceCaseDetail.value = true
      insuranceCaseDetail.value = null
      const viewLoad = beginViewLoad()

      try {
        const response: GetInsuranceCaseDetailResponse =
          await customerDataService.getInsuranceCaseDetail(clientId, caseId)
        if (droppedViewLoad(viewLoad)) return
        if (response.success) {
          insuranceCaseDetail.value = response.insuranceCase || null

          // Extract hierarchy data from loaded insurance case and update routeContext
          if (insuranceCaseDetail.value) {
            updateRouteContext(
              insuranceCaseDetail.value.contractId,
              insuranceCaseDetail.value.contractType,
              insuranceCaseDetail.value.subcontractId,
            )
          }

          // Now ensure parent context is loaded after routeContext is updated
          await ensureParentContext('insuranceCase')
        } else {
          // Legacy response format
          throw Error(response.message || 'Failed to fetch insurance case detail')
        }
      } catch (err) {
        if (droppedViewLoad(viewLoad, err)) return
        handleStoreError(err as Error, {
          errorType: 'loadInsuranceCaseDetail',
        })
        insuranceCaseDetail.value = null
      } finally {
        if (viewLoad.current()) {
          isLoadingHierarchy.value = false
          loading.insuranceCaseDetail.value = false
        }
      }
    }

    async function loadPayments(): Promise<void> {
      if (!isAuthenticated.value) {
        await handleAuthFailure()
        payments.value = null
        return
      }

      const clientId = requireClientIdOrRedirectToRoot()
      if (clientId === null) {
        payments.value = null
        return
      }

      // Payments are loaded for the contract (smlouva). A subcontract id is not a smlouva id
      // and the payments endpoint answers 403, which blocks the subcontract page.
      const contractId = routeContext.value.contractId ?? null

      if (!contractId) {
        payments.value = null
        return
      }

      loading.payments.value = true
      const viewLoad = beginViewLoad()

      try {
        const response: GetPaymentsResponse = await customerDataService.getPayments(
          clientId,
          contractId,
        )
        if (droppedViewLoad(viewLoad)) return
        if (response.success) {
          payments.value = response.payments || null
        } else {
          throw Error(response.message || 'Failed to fetch payments')
        }
      } catch (err) {
        if (droppedViewLoad(viewLoad, err)) return
        handleStoreError(err as Error, {
          errorType: 'loadPayments',
        })
        payments.value = null
      } finally {
        if (viewLoad.current()) loading.payments.value = false
      }
    }

    // Message filters are currently handled server-side; the filters parameter is accepted
    // for consistency with other loaders (e.g. insurance cases) but not yet forwarded.
    async function loadMessages(filters?: MessageStatusFilterState): Promise<void> {
      if (!isAuthenticated.value) {
        await handleAuthFailure()
        messages.value = null
        return
      }

      const clientId = requireClientIdOrRedirectToRoot()
      if (clientId === null) {
        messages.value = null
        return
      }

      const caseId = routeContext.value.caseId
      const subcontractId = routeContext.value.subcontractId
      const contractId = routeContext.value.contractId

      loading.messages.value = true
      const viewLoad = beginViewLoad()

      try {
        let response: GetMessagesResponse
        if (caseId) {
          response = await customerDataService.getInsuranceCaseMessages(clientId, caseId)
        } else if (subcontractId) {
          response = await customerDataService.getSubcontractMessages(clientId, subcontractId)
        } else if (contractId) {
          response = await customerDataService.getContractMessages(clientId, contractId)
        } else {
          // Forward archived filter to API (only supported by general messages endpoint)
          response = await customerDataService.getMessages(clientId, filters?.archived)
        }

        if (droppedViewLoad(viewLoad)) return
        if (response.success) {
          const _filters = filters || { created: true, read: true, archived: false }
          messages.value = (response.messages || []).filter((msg) => {
            return (
              (_filters.created && msg.status === MessageStatus.created) ||
              (_filters.read && msg.status === MessageStatus.read) ||
              (_filters.archived && msg.status === MessageStatus.archived)
            )
          })
        } else {
          throw Error(response.message || 'Failed to fetch messages')
        }
      } catch (err) {
        if (droppedViewLoad(viewLoad, err)) return
        handleStoreError(err as Error, {
          errorType: 'loadMessages',
        })
        messages.value = null
      } finally {
        if (viewLoad.current()) loading.messages.value = false
      }
    }

    async function loadSearch(
      clientId: number,
      text: string,
      target: SearchTarget | null = null,
    ): Promise<void> {
      if (!isAuthenticated.value) {
        await handleAuthFailure()
        searchResults.value = null
        return
      }

      loading.search.value = true
      const viewLoad = beginViewLoad()

      try {
        const response = await customerDataService.search(clientId, text, target)
        if (droppedViewLoad(viewLoad)) return
        if (response.success) {
          searchResults.value = {
            contracts: response.contracts || [],
            insuranceCases: response.insuranceCases || [],
          }
        } else {
          throw Error(response.message || 'Failed to perform search')
        }
      } catch (err) {
        if (droppedViewLoad(viewLoad, err)) return
        handleStoreError(err as Error, {
          errorType: 'loadSearch',
        })
        searchResults.value = null
      } finally {
        if (viewLoad.current()) loading.search.value = false
      }
    }

    async function fetchClientsFromApi(): Promise<void> {
      const viewLoad = beginViewLoad()
      try {
        const response: GetClientsForCustomerResponse =
          await customerDataService.getClientsForCustomer()
        if (droppedViewLoad(viewLoad)) return
        if (response.success) {
          clients.value = response.clients || []

          // Try to restore clientId from localStorage first
          let restoredClientId: number | null = null
          if (typeof window !== 'undefined') {
            try {
              const storedClientId = localStorage.getItem('selectedClientId')
              if (storedClientId) {
                const parsedId = Number(storedClientId)
                // Validate that the stored clientId exists in the loaded clients
                if (!isNaN(parsedId) && clients.value?.some((c) => c.id === parsedId)) {
                  restoredClientId = parsedId
                }
              }
            } catch {
              // ignore storage errors
            }
          }

          // If no clientId in route context, try restored value, then first client
          if (!routeContext.value.clientId) {
            if (restoredClientId !== null) {
              routeContext.value.clientId = restoredClientId
            } else if (clients.value?.[0]) {
              routeContext.value.clientId = clients.value[0].id
            }
          }
        } else {
          throw Error(response.message || 'Failed to fetch clients')
        }
      } catch (err) {
        if (droppedViewLoad(viewLoad, err)) return
        handleStoreError(err as Error, {
          errorType: 'loadClients',
        })
        clients.value = null
      }
    }

    /**
     * Loads the client list when the UI or validation needs `clients` / `selectedClient`.
     * Not used for API calls that only need routeContext.clientId.
     */
    async function ensureClientsLoaded(): Promise<void> {
      if (!isAuthenticated.value) {
        await handleAuthFailure()
        clients.value = null
        return
      }
      if (clients.value !== null) {
        return
      }
      if (!clientsLoadInFlight) {
        clientsLoadInFlight = fetchClientsFromApi().finally(() => {
          clientsLoadInFlight = null
        })
      }
      await clientsLoadInFlight
    }

    /** Blocking async accessor — await before reading `clients` when callers are outside store actions. */
    async function getClients(): Promise<Client[]> {
      await ensureClientsLoaded()
      return clients.value ?? []
    }

    async function loadClients(): Promise<void> {
      await ensureClientsLoaded()
    }

    async function loadClientInformation(): Promise<void> {
      if (!isAuthenticated.value) {
        await handleAuthFailure()
        clientInformation.value = null
        return
      }

      const clientId = requireClientIdOrRedirectToRoot()
      if (clientId === null) {
        clientInformation.value = null
        return
      }

      loading.clientInformation.value = true
      const viewLoad = beginViewLoad()

      try {
        const response: GetClientInformationResponse =
          await customerDataService.getClientInformation(clientId)
        if (droppedViewLoad(viewLoad)) return
        if (response.success) {
          clientInformation.value = response.clientInformation || null
        } else {
          throw Error(response.message || 'Failed to fetch client information')
        }
      } catch (err) {
        if (droppedViewLoad(viewLoad, err)) return
        handleStoreError(err as Error, {
          errorType: 'loadClientInformation',
        })
        clientInformation.value = null
      } finally {
        if (viewLoad.current()) loading.clientInformation.value = false
      }
    }

    async function loadDashboardInfo(): Promise<void> {
      if (!isAuthenticated.value) {
        await handleAuthFailure()
        dashboardInfo.value = null
        return
      }

      const clientId = requireClientIdOrRedirectToRoot()
      if (clientId === null) {
        dashboardInfo.value = null
        return
      }

      loading.dashboard.value = true
      const viewLoad = beginViewLoad()

      try {
        const response = await customerDataService.getDashboardInfo(clientId)
        if (droppedViewLoad(viewLoad)) return
        if (response.success) {
          dashboardInfo.value = response
        } else {
          throw Error(response.message || 'Failed to fetch dashboard info')
        }
      } catch (err) {
        if (droppedViewLoad(viewLoad, err)) return
        handleStoreError(err as Error, {
          errorType: 'loadDashboardInfo',
        })
        dashboardInfo.value = null
      } finally {
        if (viewLoad.current()) loading.dashboard.value = false
      }
    }

    function setSelectedClient(client: Client): void {
      routeContext.value.clientId = client.id
    }

    function setRouteContext(context: RouteContext): void {
      routeContext.value = { ...context }
    }

    function clearRouteContext(): void {
      routeContext.value = {
        clientId: null,
        contractId: null,
        contractType: null,
        subcontractId: null,
        caseId: null,
      }
    }

    async function getDocumentUrl(documentUid: string): Promise<string> {
      if (!isAuthenticated.value) {
        await handleAuthFailure()
        return ''
      }

      try {
        const url = await customerDataService.getDocumentUrl(documentUid)
        return url
      } catch (err) {
        handleStoreError(err as Error, {
          errorType: 'getDocumentUrl',
        })
      }
      return ''
    }

    async function getUploadUrl(): Promise<string> {
      if (!isAuthenticated.value) {
        await handleAuthFailure()
        return ''
      }

      try {
        const url = await customerDataService.getUploadUrl()
        return url
      } catch (err) {
        handleStoreError(err as Error, {
          errorType: 'getUploadUrl',
        })
      }
      return ''
    }

    async function deleteUploadedFile(
      uid: string,
    ): Promise<{ success: boolean; message?: string }> {
      if (!isAuthenticated.value) {
        await handleAuthFailure()
        return { success: false, message: 'Not authenticated' }
      }

      try {
        const result = await customerDataService.deleteUploadedFile(uid)
        return result
      } catch (err) {
        handleStoreError(err as Error, {
          errorType: 'deleteUploadedFile',
        })
        return { success: false, message: 'An unexpected error occurred' }
      }
    }

    async function changePassword(
      currentPassword: string,
      newPassword: string,
    ): Promise<{ success: boolean; message?: string }> {
      if (!isAuthenticated.value) {
        await handleAuthFailure()
        return { success: false, message: 'Not authenticated' }
      }

      loading.changePassword.value = true
      try {
        const result = await customerDataService.changePassword(currentPassword, newPassword)
        return result
      } catch (err) {
        handleStoreError(err as Error, {
          errorType: 'changePassword',
        })
        const message = err instanceof Error ? err.message : 'An unexpected error occurred'
        return { success: false, message }
      } finally {
        loading.changePassword.value = false
      }
    }

    function clearMessageData(): void {
      message.value = {
        title: '',
        text: '',
        attachments: [],
      }
    }

    async function submitMessage(): Promise<{ success: boolean; message?: string }> {
      if (!isAuthenticated.value) {
        await handleAuthFailure()
        return { success: false, message: 'Not authenticated' }
      }

      const clientId = requireClientIdOrRedirectToRoot()
      if (clientId === null) {
        return { success: false, message: 'No client selected' }
      }

      if (!message.value.title.trim() || !message.value.text.trim()) {
        return { success: false, message: 'Title and text are required' }
      }

      loading.message.value = true

      try {
        const contractType = routeContext.value.subcontractId
          ? ContractType.subcontract
          : routeContext.value.contractType
        const contractId = routeContext.value.subcontractId
          ? routeContext.value.subcontractId
          : routeContext.value.contractId

        const response = await customerDataService.submitMessage(
          clientId,
          message.value.title,
          message.value.text,
          contractType,
          contractId,
          routeContext.value.caseId,
          message.value.attachments
            .map((att) => ({
              fileGuid: (att.response as { fileGuid: string })?.fileGuid || '',
              fileName: att.name,
            }))
            .filter((att) => att.fileGuid !== ''),
        )

        if (response.success) {
          clearMessageData()
          return { success: true }
        } else {
          return { success: false, message: response.message || 'Failed to submit message' }
        }
      } catch (err) {
        handleStoreError(err as Error, {
          errorType: 'submitMessage',
        })
        return { success: false, message: 'An unexpected error occurred' }
      } finally {
        loading.message.value = false
      }
    }

    async function setMessageRead(
      messageId: number,
    ): Promise<{ success: boolean; message?: string }> {
      if (!isAuthenticated.value) {
        await handleAuthFailure()
        return { success: false, message: 'Not authenticated' }
      }

      const clientId = requireClientIdOrRedirectToRoot()
      if (clientId === null) {
        return { success: false, message: 'No client selected' }
      }

      if (!messageId) {
        return { success: false, message: 'Message ID is required' }
      }

      try {
        const response: SetMessageStatusResponse = await customerDataService.setMessageStatus(
          clientId,
          messageId,
          MessageStatus.read,
        )

        if (response.success) {
          // Update local messages array if message exists
          if (messages.value) {
            const messageIndex = messages.value.findIndex((msg) => msg.id === messageId)
            if (messageIndex !== -1) {
              messages.value[messageIndex] = response.changedMessage || messages.value[messageIndex]
            }
          }
          return { success: true }
        } else {
          return { success: false, message: response.message || 'Failed to set message status' }
        }
      } catch (err) {
        handleStoreError(err as Error, {
          errorType: 'setMessageRead',
        })
        return { success: false, message: 'An unexpected error occurred' }
      }
    }

    async function setMessageArchived(
      messageId: number,
      filters?: MessageStatusFilterState,
    ): Promise<{ success: boolean; message?: string }> {
      if (!isAuthenticated.value) {
        await handleAuthFailure()
        return { success: false, message: 'Not authenticated' }
      }

      const clientId = requireClientIdOrRedirectToRoot()
      if (clientId === null) {
        return { success: false, message: 'No client selected' }
      }

      if (!messageId) {
        return { success: false, message: 'Message ID is required' }
      }

      try {
        const response: SetMessageStatusResponse = await customerDataService.setMessageStatus(
          clientId,
          messageId,
          MessageStatus.archived,
        )

        if (response.success) {
          // Update local messages array if message exists
          if (messages.value) {
            const messageIndex = messages.value.findIndex((msg) => msg.id === messageId)
            if (messageIndex !== -1) {
              // If filters are provided and archived filter is not selected, remove the message
              // (since server-side filtering would exclude it)
              if (
                filters &&
                filters.archived === false &&
                response.changedMessage?.status === MessageStatus.archived
              ) {
                messages.value.splice(messageIndex, 1)
              } else {
                // Otherwise, update the message status
                messages.value[messageIndex] =
                  response.changedMessage || messages.value[messageIndex]
              }
            }
          }
          return { success: true }
        } else {
          return { success: false, message: response.message || 'Failed to set message status' }
        }
      } catch (err) {
        handleStoreError(err as Error, {
          errorType: 'setMessageArchived',
        })
        return { success: false, message: 'An unexpected error occurred' }
      }
    }

    // Clear contract-related data when client changes
    function clearData(): void {
      contracts.value = null
      dashboardInfo.value = null
      clientInformation.value = null
      contractDetail.value = null
      subContractDetail.value = null
      contractDocuments.value = null
      generalAttachments.value = null
      clientAttachments.value = null
      messages.value = null
    }

    watch(routeContext, (newContext, oldContext) => {
      if (newContext && newContext !== oldContext) {
        if (newContext.contractId == null) {
          contractDetail.value = null
        }
        if (newContext.caseId == null) {
          insuranceCaseDetail.value = null
        }
        if (newContext.subcontractId == null) {
          subContractDetail.value = null
        }
        // Clear messages when context changes
        messages.value = null
      }
    })

    // Watch for selectedClient changes, clear data and trigger reload
    watch(selectedClient, (newClient, oldClient) => {
      if (newClient && newClient !== oldClient) {
        clearData()
        clientChangeTrigger.value++ // Increment to trigger watchers in components
      }
    })

    // Persist clientId to localStorage when routeContext.clientId changes
    watch(
      () => routeContext.value.clientId,
      (newClientId) => {
        if (typeof window !== 'undefined' && newClientId !== null) {
          try {
            localStorage.setItem('selectedClientId', newClientId.toString())
          } catch {
            // ignore storage errors
          }
        }
      },
    )

    watch(
      isAuthenticated,
      async (authenticated) => {
        if (!authenticated) {
          clearData()
          clients.value = null
        }
      },
      { immediate: true },
    )

    // Computed property for selected client ID - derived from route context only
    const selectedClientId = computed({
      get: () => routeContext.value.clientId ?? 0,
      set: (value: number | null) => {
        if (value) {
          routeContext.value.clientId = value
        } else {
          routeContext.value.clientId = null
        }
      },
    })

    return {
      // State
      contracts,
      contractDetail,
      subContractDetail,
      contractDocuments,
      messageAttachments,
      generalAttachments,
      clientAttachments,
      subcontracts,
      insuranceCases,
      insuranceCaseDetail,
      payments,
      messages,
      searchResults,
      message,
      isLoadingContracts,
      isLoadingContractDetail,
      isLoadingSubContractDetail,
      isLoadingContractDocuments,
      isLoadingMessageAttachments,
      isLoadingGeneralAttachments,
      isLoadingClientAttachments,
      isLoadingMeeting,
      isLoadingDashboard,
      isLoadingClientInformation,
      isLoadingSubcontracts,
      isLoadingInsuranceCases,
      isLoadingInsuranceCaseDetail,
      isLoadingPayments,
      isLoadingSearch,
      isLoadingMessage,
      isLoadingMessages,
      isLoadingChangePassword,
      accountManager,
      contractsOverview,
      insuranceOverview,
      messagesOverview,
      noticeBoard,
      selectedClient,
      selectedClientId,
      clients,
      clientInformation,
      // Actions
      loadClients,
      ensureClientsLoaded,
      getClients,
      loadClientInformation,
      loadContracts,
      loadContractDetail,
      loadDocuments,
      loadMessageAttachments,
      loadGeneralAttachments,
      loadClientAttachments,
      loadSubContractDetail,
      loadSubcontracts,
      loadInsuranceCases,
      loadInsuranceCaseDetail,
      loadPayments,
      loadMessages,
      loadSearch,
      loadDashboardInfo,
      clearData,
      setSelectedClient,
      getDocumentUrl,
      setRouteContext,
      clearRouteContext,
      routeContext,
      isLoadingHierarchy,
      clearMessageData,
      submitMessage,
      setMessageRead,
      setMessageArchived,
      getUploadUrl,
      deleteUploadedFile,
      changePassword,
    }
  })()

/**
 * Composable that provides access to the customer store with proper dependency injection
 * This ensures that the store is properly initialized and reactive across components
 */
export function useCustomerStore() {
  const customerDataService = inject(TYPES.CUSTOMER_DATA_SERVICE) as CustomerDataService

  if (!customerDataService) {
    throw new Error('CustomerDataService not found in dependency injection container')
  }

  return createCustomerStore(customerDataService)
}

export type { FilterState, CaseFilterState, RouteContext, MessageStatusFilterState }
